import React, { useState, useEffect, useCallback } from 'react';
import { Eye, Settings, FileText, Users, RefreshCw, AlertCircle } from 'lucide-react';
import FBMonitorStats from './FBMonitorStats';
import SessionSetup from './SessionSetup';
import SessionStatusBanner from './SessionStatusBanner';
import SourcesManager from './SourcesManager';
import FBMonitorSettings from './FBMonitorSettings';
import ReportsView from './ReportsView';
import * as fbMonitorApi from '../../api/fbMonitor';

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: Eye },
  { id: 'sources', label: 'Sources', icon: Users },
  { id: 'settings', label: 'Settings', icon: Settings },
  { id: 'reports', label: 'Reports', icon: FileText },
];

const FBMonitorDashboard = ({ userEmail, subscription }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sessionStatus, setSessionStatus] = useState(null);
  const [sources, setSources] = useState([]);
  const [sourcesLimit, setSourcesLimit] = useState(10);
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSessionSetup, setShowSessionSetup] = useState(false);

  // Load initial data
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [sessionData, sourcesData, configData, statsData] = await Promise.all([
        fbMonitorApi.getSessionStatus(),
        fbMonitorApi.getSources().catch(() => ({ sources: [], total: 0, limit: 10 })),
        fbMonitorApi.getConfig().catch(() => null),
        fbMonitorApi.getStats().catch(() => null),
      ]);

      setSessionStatus(sessionData);
      setSources(sourcesData.sources || []);
      setSourcesLimit(sourcesData.limit || 10);
      setConfig(configData);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to load FB Monitor data:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Session handlers
  const handleSessionCreated = useCallback(async () => {
    setShowSessionSetup(false);
    await loadData();
  }, [loadData]);

  const handleReconnect = useCallback(() => {
    setShowSessionSetup(true);
  }, []);

  const handleDisconnect = useCallback(async () => {
    try {
      await fbMonitorApi.deleteSession();
      setSessionStatus({ status: 'no_session', expires_at: null, days_remaining: null });
      setSources([]);
      setConfig(null);
    } catch (err) {
      console.error('Failed to disconnect session:', err);
      setError(err.message);
    }
  }, []);

  // Source handlers
  const handleSourceAdded = useCallback(async (newSource) => {
    setSources(prev => [...prev, newSource]);
    // Refresh stats
    const statsData = await fbMonitorApi.getStats().catch(() => null);
    setStats(statsData);
  }, []);

  const handleSourceUpdated = useCallback((updatedSource) => {
    setSources(prev => prev.map(s => s.id === updatedSource.id ? updatedSource : s));
  }, []);

  const handleSourceDeleted = useCallback(async (sourceId) => {
    setSources(prev => prev.filter(s => s.id !== sourceId));
    // Refresh stats
    const statsData = await fbMonitorApi.getStats().catch(() => null);
    setStats(statsData);
  }, []);

  // Config handlers
  const handleConfigUpdated = useCallback((updatedConfig) => {
    setConfig(updatedConfig);
  }, []);

  // Determine UI state
  const getUIState = () => {
    if (!sessionStatus) return 'loading';
    if (sessionStatus.status === 'no_session') return 'no_session';
    if (sessionStatus.status === 'expired') return 'expired';
    if (sessionStatus.status === 'expiring_soon') return 'expiring_soon';
    if (sources.length === 0) return 'no_sources';
    if (config && !config.is_active) return 'inactive';
    return 'active';
  };

  const uiState = getUIState();

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="w-8 h-8 text-sky-500 animate-spin" />
          <p className="text-slate-400">Loading FB Monitor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold dark:text-white text-slate-900 mb-2">
            Failed to Load
          </h2>
          <p className="dark:text-slate-400 text-slate-500 mb-6">{error}</p>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg font-semibold"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show session setup for new users or expired sessions
  if (uiState === 'no_session' || showSessionSetup) {
    return (
      <div className="flex-1 flex flex-col overflow-auto">
        <SessionSetup
          onSessionCreated={handleSessionCreated}
          onCancel={() => setShowSessionSetup(false)}
          isReconnect={uiState !== 'no_session'}
        />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Session Status Banner */}
      {(uiState === 'expired' || uiState === 'expiring_soon') && (
        <SessionStatusBanner
          status={sessionStatus.status}
          expiresAt={sessionStatus.expires_at}
          daysRemaining={sessionStatus.days_remaining}
          onReconnect={handleReconnect}
        />
      )}

      {/* Tab Navigation */}
      <div className="flex items-center gap-1 px-4 py-3 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === tab.id
                ? 'bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300'
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
        <div className="flex-1" />
        <button
          onClick={loadData}
          className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto p-6">
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            <FBMonitorStats
              stats={stats}
              sessionStatus={sessionStatus}
              sourcesCount={sources.filter(s => s.is_active).length}
            />

            {/* Quick Status */}
            {uiState === 'no_sources' && (
              <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6 text-center">
                <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-2">
                  No Sources Added
                </h3>
                <p className="dark:text-slate-400 text-slate-500 mb-4">
                  Add Facebook groups or pages to start monitoring for mortgage leads.
                </p>
                <button
                  onClick={() => setActiveTab('sources')}
                  className="px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg font-semibold"
                >
                  Add Your First Source
                </button>
              </div>
            )}

            {uiState === 'inactive' && (
              <div className="dark:bg-amber-500/10 bg-amber-50 border dark:border-amber-500/30 border-amber-200 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-semibold dark:text-amber-400 text-amber-700 mb-1">
                      Monitoring Paused
                    </h3>
                    <p className="dark:text-amber-300/80 text-amber-600 mb-3">
                      Your FB Monitor is currently paused. Enable it in settings to resume daily reports.
                    </p>
                    <button
                      onClick={() => setActiveTab('settings')}
                      className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-white rounded-lg font-semibold"
                    >
                      Go to Settings
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'sources' && (
          <SourcesManager
            sources={sources}
            sourcesLimit={sourcesLimit}
            onSourceAdded={handleSourceAdded}
            onSourceUpdated={handleSourceUpdated}
            onSourceDeleted={handleSourceDeleted}
          />
        )}

        {activeTab === 'settings' && (
          <FBMonitorSettings
            config={config}
            userEmail={userEmail}
            onConfigUpdated={handleConfigUpdated}
            onDisconnect={handleDisconnect}
          />
        )}

        {activeTab === 'reports' && (
          <ReportsView />
        )}
      </div>
    </div>
  );
};

export default FBMonitorDashboard;
